import { defaultLoopingAnimationItem } from "../looping-animation-helpers";
import type { EffectPreset, EffectPresetCategory } from "./effects-presets-types";

export interface EffectPresetCategoryLabel {
  value: EffectPresetCategory;
  label: string;
}

/**
 * 65 ready-to-apply effect presets that ship with the styling panel. Every
 * preset only references fields that the Effects panel actually edits
 * (opacity, mix-blend-mode, cursor, pointer-events, outline-*, transform-*,
 * filter / backdrop-filter, transition-*). The Glow category uses the
 * `drop-shadow` filter (KOA-17421 exposed it in the Filters section); a glow
 * follows the element's alpha shape, unlike the rectangular Shadow panel.
 *
 * State-targeted presets (Hover ..., Disabled Look, Accessible Focus Ring,
 * Soft Focus Ring, Error Focus) declare `targetState` so the apply call can
 * (a) write into the right state bucket (`hoverStyles` etc.) and (b) switch
 * the styling-window's selected state mode for immediate visual feedback.
 *
 * Reveal presets carry an `initialStateHint` that applyEffectPreset derives
 * into a real entry (mount) animation; presets with character timing (e.g.
 * Pop In's overshoot bezier) declare it via `entryTiming`, others get the
 * graceful 500ms ease-out default.
 */
export const EFFECTS_PRESETS: EffectPreset[] = [
  // 1. Basic ────────────────────────────────────────────────────────────
  {
    id: "fade-in",
    name: "Fade In",
    category: "basic",
    description: "Simple opacity reveal.",
    tags: ["fade", "opacity", "reveal"],
    previewType: "fade",
    targetState: "base",
    initialStateHint: { opacity: 0 },
    recommendedFor: ["cards", "buttons", "modals", "list items"],
    payload: {
      basic: { opacity: 1 },
      transitions: [{ property: "opacity", duration: 250, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "fade-out",
    name: "Fade Out",
    category: "basic",
    description: "Softly hide the element.",
    tags: ["fade", "opacity", "hide"],
    previewType: "fade",
    targetState: "base",
    payload: {
      basic: { opacity: 0 },
      transitions: [{ property: "opacity", duration: 200, timingFunction: "ease-in", delay: 0 }]
    }
  },
  {
    id: "disabled-look",
    name: "Disabled Look",
    category: "basic",
    description: "Inactive, non-interactive surface.",
    tags: ["disabled", "inactive", "pointer-events"],
    previewType: "utility",
    targetState: "disabled",
    payload: {
      basic: { opacity: 0.45, cursor: "not-allowed", pointerEvents: "none" },
      transitions: [{ property: "opacity", duration: 150, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "clickable",
    name: "Clickable",
    category: "basic",
    description: "Marks an element as interactive.",
    tags: ["cursor", "pointer", "interactive"],
    previewType: "utility",
    targetState: "base",
    payload: {
      basic: { cursor: "pointer", pointerEvents: "auto" }
    }
  },

  // 2. Hover Effects ────────────────────────────────────────────────────
  {
    id: "hover-lift",
    name: "Hover Lift",
    category: "hover",
    description: "Card raises and brightens on hover.",
    tags: ["hover", "lift", "card"],
    previewType: "hover",
    targetState: "hover",
    recommendedFor: ["cards", "dashboard widgets", "product cards"],
    payload: {
      transforms: [{ type: "move", x: 0, y: -6, z: 0, unit: "px" }],
      filters: [{ type: "brightness", value: 1.04, unit: "none" }],
      transitions: [
        { property: "transform", duration: 220, timingFunction: "ease-out", delay: 0 },
        { property: "filter", duration: 220, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },
  {
    id: "hover-soft-scale",
    name: "Hover Soft Scale",
    category: "hover",
    description: "Subtle grow on hover.",
    tags: ["hover", "scale", "grow"],
    previewType: "hover",
    targetState: "hover",
    recommendedFor: ["buttons", "icons", "cards"],
    payload: {
      transforms: [{ type: "scale", x: 1.03, y: 1.03, z: 1, uniformScale: true, unit: "none" }],
      transitions: [{ property: "transform", duration: 180, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "hover-pressable",
    name: "Hover Pressable",
    category: "hover",
    description: "Slight press-down feel.",
    tags: ["hover", "press", "scale-down"],
    previewType: "hover",
    targetState: "hover",
    recommendedFor: ["buttons", "chips", "icon buttons"],
    payload: {
      transforms: [{ type: "scale", x: 0.97, y: 0.97, z: 1, uniformScale: true, unit: "none" }],
      transitions: [{ property: "transform", duration: 120, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "hover-brighten",
    name: "Hover Brighten",
    category: "hover",
    description: "Liven media on hover.",
    tags: ["hover", "brightness", "saturate"],
    previewType: "image",
    targetState: "hover",
    recommendedFor: ["images", "thumbnails", "media cards"],
    payload: {
      filters: [
        { type: "brightness", value: 1.12, unit: "none" },
        { type: "saturate", value: 1.08, unit: "none" }
      ],
      transitions: [{ property: "filter", duration: 220, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "hover-dim",
    name: "Hover Dim",
    category: "hover",
    description: "Darken element on hover.",
    tags: ["hover", "dim", "darken"],
    previewType: "image",
    targetState: "hover",
    recommendedFor: ["overlays", "image cards", "inactive items"],
    payload: {
      filters: [{ type: "brightness", value: 0.86, unit: "none" }],
      transitions: [{ property: "filter", duration: 180, timingFunction: "ease-out", delay: 0 }]
    }
  },

  {
    id: "hover-slide-right",
    name: "Slide Right Hover",
    category: "hover",
    description: "Nudges the element right on hover.",
    tags: ["hover", "slide", "menu", "nav", "list"],
    previewType: "hover",
    targetState: "hover",
    recommendedFor: ["sidebars", "list items", "navigation links"],
    payload: {
      transforms: [{ type: "move", x: 6, y: 0, z: 0, unit: "px" }],
      transitions: [{ property: "transform", duration: 180, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "hover-skew-accent",
    name: "Skew Accent Hover",
    category: "hover",
    description: "Small energetic skew on hover.",
    tags: ["hover", "skew", "accent", "cta", "modern"],
    previewType: "hover",
    targetState: "hover",
    recommendedFor: ["CTA buttons", "badges", "cards"],
    payload: {
      transforms: [
        { type: "skew", x: -2, y: 0, unit: "deg" },
        { type: "scale", x: 1.015, y: 1.015, z: 1, uniformScale: true, unit: "none" }
      ],
      transitions: [{ property: "transform", duration: 180, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "pressed-depth",
    name: "Pressed Depth",
    category: "hover",
    description: "Tactile press-down feedback while clicking.",
    tags: ["pressed", "button", "tap", "depth", "interaction"],
    previewType: "hover",
    targetState: "pressed",
    recommendedFor: ["buttons", "cards", "menu items"],
    payload: {
      transforms: [
        { type: "move", x: 0, y: 1, z: 0, unit: "px" },
        { type: "scale", x: 0.985, y: 0.985, z: 1, uniformScale: true, unit: "none" }
      ],
      filters: [{ type: "brightness", value: 0.96, unit: "none" }],
      transitions: [
        { property: "transform", duration: 120, timingFunction: "ease-out", delay: 0 },
        { property: "filter", duration: 120, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },

  // 3. Entrance / Reveal ────────────────────────────────────────────────
  {
    id: "slide-up-reveal",
    name: "Slide Up Reveal",
    category: "reveal",
    description: "Final state for a bottom-to-top reveal.",
    tags: ["slide", "reveal", "up"],
    previewType: "reveal",
    targetState: "base",
    initialStateHint: { opacity: 0, translateY: 16 },
    recommendedFor: ["sections", "cards", "dashboard blocks"],
    payload: {
      basic: { opacity: 1 },
      transforms: [{ type: "move", x: 0, y: 0, z: 0, unit: "px" }],
      transitions: [
        { property: "opacity", duration: 280, timingFunction: "ease-out", delay: 0 },
        { property: "transform", duration: 280, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },
  {
    id: "slide-down-reveal",
    name: "Slide Down Reveal",
    category: "reveal",
    description: "Final state for a top-to-bottom reveal.",
    tags: ["slide", "reveal", "down"],
    previewType: "reveal",
    targetState: "base",
    initialStateHint: { opacity: 0, translateY: -16 },
    payload: {
      basic: { opacity: 1 },
      transforms: [{ type: "move", x: 0, y: 0, z: 0, unit: "px" }],
      transitions: [
        { property: "opacity", duration: 280, timingFunction: "ease-out", delay: 0 },
        { property: "transform", duration: 280, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },
  {
    id: "slide-left-reveal",
    name: "Slide Left Reveal",
    category: "reveal",
    description: "Final state for a right-to-left reveal.",
    tags: ["slide", "reveal", "left"],
    previewType: "reveal",
    targetState: "base",
    initialStateHint: { opacity: 0, translateX: 24 },
    payload: {
      basic: { opacity: 1 },
      transforms: [{ type: "move", x: 0, y: 0, z: 0, unit: "px" }],
      transitions: [
        { property: "opacity", duration: 300, timingFunction: "ease-out", delay: 0 },
        { property: "transform", duration: 300, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },
  {
    id: "scale-reveal",
    name: "Scale Reveal",
    category: "reveal",
    description: "Final state for a grow-in reveal.",
    tags: ["scale", "reveal", "zoom"],
    previewType: "reveal",
    targetState: "base",
    initialStateHint: { opacity: 0, scale: 0.94 },
    payload: {
      basic: { opacity: 1 },
      transforms: [{ type: "scale", x: 1, y: 1, z: 1, uniformScale: true, unit: "none" }],
      transitions: [
        { property: "opacity", duration: 240, timingFunction: "ease-out", delay: 0 },
        { property: "transform", duration: 240, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },

  {
    id: "pop-in-reveal",
    name: "Pop In Reveal",
    category: "reveal",
    description: "Soft overshoot pop and fade on mount.",
    tags: ["reveal", "pop", "scale", "entry", "modal"],
    previewType: "reveal",
    targetState: "base",
    initialStateHint: { opacity: 0, scale: 0.88 },
    // Overshoot bezier IS the "pop" — without entryTiming the derived entry
    // animation would flatten to the 500ms ease-out default.
    entryTiming: { durationMs: 420, timingFunction: "cubic-bezier(0.16,1,0.3,1)" },
    recommendedFor: ["modals", "empty states", "cards", "badges"],
    payload: {
      basic: { opacity: 1 },
      transforms: [{ type: "scale", x: 1, y: 1, z: 1, uniformScale: true, unit: "none" }],
      transitions: [
        { property: "opacity", duration: 260, timingFunction: "ease-out", delay: 0 },
        {
          property: "transform",
          duration: 260,
          timingFunction: "cubic-bezier",
          cubicBezier: [0.16, 1, 0.3, 1],
          delay: 0
        }
      ]
    }
  },
  {
    id: "slide-up-large-reveal",
    name: "Large Slide Up Reveal",
    category: "reveal",
    description: "Stronger vertical reveal for hero sections.",
    tags: ["reveal", "slide", "hero", "entry", "section"],
    previewType: "reveal",
    targetState: "base",
    initialStateHint: { opacity: 0, translateY: 40 },
    entryTiming: { durationMs: 560, timingFunction: "ease-out" },
    recommendedFor: ["hero sections", "large cards", "landing sections"],
    payload: {
      basic: { opacity: 1 },
      transforms: [{ type: "move", x: 0, y: 0, z: 0, unit: "px" }],
      transitions: [
        { property: "opacity", duration: 420, timingFunction: "ease-out", delay: 0 },
        { property: "transform", duration: 420, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },

  // 4. Cards & Surfaces ─────────────────────────────────────────────────
  {
    id: "premium-card-hover",
    name: "Premium Card Hover",
    category: "cards",
    description: "Lift + slight zoom + brightness.",
    tags: ["card", "hover", "premium"],
    previewType: "card",
    targetState: "hover",
    recommendedFor: ["SaaS cards", "analytics widgets", "CRM cards"],
    payload: {
      transforms: [
        { type: "move", x: 0, y: -4, z: 0, unit: "px" },
        { type: "scale", x: 1.01, y: 1.01, z: 1, uniformScale: true, unit: "none" }
      ],
      filters: [
        { type: "brightness", value: 1.03, unit: "none" },
        { type: "saturate", value: 1.04, unit: "none" }
      ],
      transitions: [
        { property: "transform", duration: 240, timingFunction: "ease-out", delay: 0 },
        { property: "filter", duration: 240, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },
  {
    id: "subtle-active-card",
    name: "Subtle Active Card",
    category: "cards",
    description: "Active/selected card glow.",
    tags: ["card", "active", "selected"],
    previewType: "card",
    targetState: "base",
    payload: {
      basic: { opacity: 1 },
      filters: [
        { type: "brightness", value: 1.06, unit: "none" },
        { type: "saturate", value: 1.06, unit: "none" }
      ],
      transitions: [
        { property: "opacity", duration: 180, timingFunction: "ease-out", delay: 0 },
        { property: "filter", duration: 180, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },
  {
    id: "muted-card",
    name: "Muted Card",
    category: "cards",
    description: "Secondary, recessed card look.",
    tags: ["card", "muted", "secondary"],
    previewType: "card",
    targetState: "base",
    payload: {
      basic: { opacity: 0.62 },
      filters: [
        { type: "saturate", value: 0.8, unit: "none" },
        { type: "brightness", value: 0.92, unit: "none" }
      ],
      transitions: [
        { property: "opacity", duration: 180, timingFunction: "ease-out", delay: 0 },
        { property: "filter", duration: 180, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },

  {
    id: "selected-card-outline",
    name: "Selected Card Outline",
    category: "cards",
    description: "Marks a selected card with a clean outside ring.",
    tags: ["selected", "active", "card", "outline", "state"],
    previewType: "card",
    targetState: "base",
    recommendedFor: ["selected cards", "pricing cards", "dashboard tiles"],
    payload: {
      outline: { outlineStyle: "solid", outlineWidth: 2, outlineOffset: 2, outlineColor: "rgba(59,130,246,0.85)" },
      filters: [
        { type: "brightness", value: 1.03, unit: "none" },
        { type: "saturate", value: 1.08, unit: "none" }
      ]
    }
  },

  // 5. Image Effects ────────────────────────────────────────────────────
  {
    id: "image-zoom-hover",
    name: "Image Zoom Hover",
    category: "image",
    description: "Image scales up on hover.",
    tags: ["image", "zoom", "hover"],
    previewType: "image",
    targetState: "hover",
    recommendedFor: ["thumbnails", "product images", "gallery items"],
    payload: {
      transforms: [{ type: "scale", x: 1.06, y: 1.06, z: 1, uniformScale: true, unit: "none" }],
      transitions: [{ property: "transform", duration: 320, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "cinematic-image",
    name: "Cinematic Image",
    category: "image",
    description: "Punchy contrast and saturation.",
    tags: ["image", "cinematic", "contrast"],
    previewType: "image",
    targetState: "base",
    payload: {
      filters: [
        { type: "contrast", value: 1.08, unit: "none" },
        { type: "saturate", value: 1.12, unit: "none" },
        { type: "brightness", value: 0.96, unit: "none" }
      ],
      transitions: [{ property: "filter", duration: 240, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "black-and-white",
    name: "Black & White",
    category: "image",
    description: "Full desaturation.",
    tags: ["image", "grayscale", "monochrome"],
    previewType: "image",
    targetState: "base",
    payload: {
      filters: [{ type: "grayscale", value: 100, unit: "%" }],
      transitions: [{ property: "filter", duration: 220, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "vintage-image",
    name: "Vintage Image",
    category: "image",
    description: "Sepia with muted saturation.",
    tags: ["image", "sepia", "vintage"],
    previewType: "image",
    targetState: "base",
    payload: {
      filters: [
        { type: "sepia", value: 35, unit: "%" },
        { type: "saturate", value: 0.85, unit: "none" },
        { type: "contrast", value: 1.05, unit: "none" }
      ],
      transitions: [{ property: "filter", duration: 240, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "blur-preview",
    name: "Blur Preview",
    category: "image",
    description: "Apply a soft blur.",
    tags: ["image", "blur", "preview"],
    previewType: "image",
    targetState: "base",
    payload: {
      filters: [{ type: "blur", value: 6, unit: "px" }],
      transitions: [{ property: "filter", duration: 220, timingFunction: "ease-out", delay: 0 }]
    }
  },

  {
    id: "image-hover-desaturate",
    name: "Image Desaturate Hover",
    category: "image",
    description: "Mutes the image on hover for editorial emphasis.",
    tags: ["image", "hover", "desaturate", "grayscale", "media"],
    previewType: "image",
    targetState: "hover",
    recommendedFor: ["image cards", "galleries", "media tiles"],
    payload: {
      filters: [
        { type: "grayscale", value: 100, unit: "%" },
        { type: "brightness", value: 0.92, unit: "none" }
      ],
      transitions: [{ property: "filter", duration: 220, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "image-focus-zoom",
    name: "Image Focus Zoom",
    category: "image",
    description: "Zooms and boosts contrast on hover.",
    tags: ["image", "hover", "zoom", "contrast", "preview"],
    previewType: "image",
    targetState: "hover",
    recommendedFor: ["media cards", "product images", "gallery items"],
    payload: {
      transforms: [{ type: "scale", x: 1.04, y: 1.04, z: 1, uniformScale: true, unit: "none" }],
      filters: [
        { type: "contrast", value: 1.08, unit: "none" },
        { type: "saturate", value: 1.12, unit: "none" }
      ],
      transitions: [
        { property: "transform", duration: 260, timingFunction: "ease-out", delay: 0 },
        { property: "filter", duration: 260, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },

  // 6. Text Effects ─────────────────────────────────────────────────────
  {
    id: "text-emphasis",
    name: "Text Emphasis",
    category: "text",
    description: "Highlight text on focus/hover.",
    tags: ["text", "emphasis", "brightness"],
    previewType: "text",
    targetState: "hover",
    recommendedFor: ["menu items", "links", "labels"],
    payload: {
      basic: { opacity: 1 },
      // Scale carries the emphasis on ANY text color — brightness alone is
      // imperceptible on near-black text (a multiplier on ~0 stays ~0).
      transforms: [{ type: "scale", x: 1.05, y: 1.05, z: 1, uniformScale: true, unit: "none" }],
      filters: [{ type: "brightness", value: 1.18, unit: "none" }],
      transitions: [
        { property: "opacity", duration: 160, timingFunction: "ease-out", delay: 0 },
        { property: "transform", duration: 160, timingFunction: "ease-out", delay: 0 },
        { property: "filter", duration: 160, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },
  {
    id: "muted-text",
    name: "Muted Text",
    category: "text",
    description: "Secondary text feel.",
    tags: ["text", "muted", "secondary"],
    previewType: "text",
    targetState: "base",
    payload: {
      basic: { opacity: 0.6 },
      transitions: [{ property: "opacity", duration: 160, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "text-hover-lift",
    name: "Text Hover Lift",
    category: "text",
    description: "Link nudges up on hover.",
    tags: ["text", "hover", "lift"],
    previewType: "text",
    targetState: "hover",
    payload: {
      basic: { opacity: 1 },
      transforms: [{ type: "move", x: 0, y: -2, z: 0, unit: "px" }],
      transitions: [
        { property: "transform", duration: 160, timingFunction: "ease-out", delay: 0 },
        { property: "opacity", duration: 160, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },

  {
    id: "text-pressed-feedback",
    name: "Text Pressed Feedback",
    category: "text",
    description: "Subtle pressed state for links, tabs and labels.",
    tags: ["text", "pressed", "link", "menu", "feedback"],
    previewType: "text",
    targetState: "pressed",
    recommendedFor: ["links", "tabs", "sidebar labels"],
    payload: {
      basic: { opacity: 0.72 },
      transforms: [{ type: "move", x: 0, y: 1, z: 0, unit: "px" }],
      transitions: [
        { property: "opacity", duration: 100, timingFunction: "ease-out", delay: 0 },
        { property: "transform", duration: 100, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },
  {
    id: "text-warning-muted",
    name: "Warning Muted Text",
    category: "text",
    description: "Lower-priority text with a warm warning feel.",
    tags: ["text", "muted", "warning", "status", "message"],
    previewType: "text",
    targetState: "base",
    recommendedFor: ["helper text", "status labels", "warnings"],
    payload: {
      basic: { opacity: 0.78 },
      filters: [
        { type: "sepia", value: 18, unit: "%" },
        { type: "saturate", value: 1.1, unit: "none" }
      ]
    }
  },

  // 7. 3D Effects ───────────────────────────────────────────────────────
  {
    id: "card-3d-tilt",
    name: "3D Card Tilt",
    category: "threeD",
    description: "Subtle 3D rotation.",
    tags: ["3d", "tilt", "card"],
    previewType: "threeD",
    targetState: "base",
    recommendedFor: ["premium cards", "feature cards", "landing pages"],
    payload: {
      transformSettings: {
        transformStyle: "preserve-3d",
        backfaceVisibility: "hidden",
        perspective: 900,
        childrenPerspective: 900,
        childrenPerspectiveOriginX: 50,
        childrenPerspectiveOriginY: 50
      },
      transforms: [{ type: "rotate", x: 8, y: -10, z: 0, unit: "deg" }],
      transitions: [{ property: "transform", duration: 260, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "flip-ready",
    name: "Flip Ready",
    category: "threeD",
    // Was a base-targeted "context only" preset (preserve-3d + transition, no
    // visible change) — it read as broken in the browser AND on the canvas.
    // Now it also tilts open on hover, so the 3D context is demonstrably live.
    description: "Flip-card 3D context — tilts open on hover.",
    tags: ["3d", "flip", "card", "hover"],
    previewType: "threeD",
    targetState: "hover",
    recommendedFor: ["flip cards", "gallery cards", "profile cards"],
    payload: {
      transformSettings: {
        transformStyle: "preserve-3d",
        backfaceVisibility: "hidden",
        transformOriginX: 50,
        transformOriginY: 50,
        perspective: 800
      },
      transforms: [{ type: "rotate", x: 0, y: -22, z: 0, unit: "deg" }],
      transitions: [{ property: "transform", duration: 400, timingFunction: "ease-in-out", delay: 0 }]
    }
  },
  {
    id: "rotate-in",
    name: "Rotate In",
    category: "threeD",
    description: "Final state for a rotate-in reveal.",
    tags: ["rotate", "reveal", "3d"],
    previewType: "threeD",
    targetState: "base",
    initialStateHint: { opacity: 0, rotateZ: -8, scale: 0.96 },
    payload: {
      basic: { opacity: 1 },
      transforms: [
        { type: "rotate", x: 0, y: 0, z: 0, unit: "deg" },
        { type: "scale", x: 1, y: 1, z: 1, uniformScale: true, unit: "none" }
      ],
      transitions: [
        { property: "opacity", duration: 280, timingFunction: "ease-out", delay: 0 },
        { property: "transform", duration: 280, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },

  {
    id: "hover-3d-tilt",
    name: "3D Tilt Hover",
    category: "threeD",
    description: "Light 3D tilt only while hovering.",
    tags: ["3d", "hover", "tilt", "card", "depth"],
    previewType: "threeD",
    targetState: "hover",
    recommendedFor: ["cards", "feature tiles", "dashboard widgets"],
    payload: {
      transformSettings: {
        transformOriginX: 50,
        transformOriginY: 50,
        transformStyle: "preserve-3d",
        backfaceVisibility: "hidden",
        perspective: 900
      },
      transforms: [{ type: "rotate", x: 4, y: -5, z: 0, unit: "deg" }],
      transitions: [{ property: "transform", duration: 220, timingFunction: "ease-out", delay: 0 }]
    }
  },

  // 8. Focus / Outline ──────────────────────────────────────────────────
  {
    id: "accessible-focus-ring",
    name: "Accessible Focus Ring",
    category: "outline",
    description: "Visible focus indicator.",
    tags: ["focus", "outline", "a11y"],
    previewType: "outline",
    targetState: "focused",
    recommendedFor: ["inputs", "buttons", "links", "accessibility"],
    payload: {
      // Outline-only payload. Kuika's transition property whitelist does not
      // include `outline-*`, so animating the ring is not supported here;
      // focus rings appear instantly, which is the accessible default.
      outline: { outlineStyle: "solid", outlineWidth: 2, outlineOffset: 3, outlineColor: "#3b82f6" }
    }
  },
  {
    id: "soft-focus-ring",
    name: "Soft Focus Ring",
    category: "outline",
    description: "Gentler focus indicator.",
    tags: ["focus", "outline", "soft"],
    previewType: "outline",
    targetState: "focused",
    payload: {
      basic: { opacity: 1 },
      outline: { outlineStyle: "solid", outlineWidth: 1, outlineOffset: 4, outlineColor: "#94a3b8" },
      transitions: [{ property: "opacity", duration: 140, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "error-focus",
    name: "Error Focus",
    category: "outline",
    description: "Error focus indicator.",
    tags: ["focus", "error", "validation"],
    previewType: "outline",
    targetState: "focused",
    payload: {
      outline: { outlineStyle: "solid", outlineWidth: 2, outlineOffset: 2, outlineColor: "#ef4444" }
    }
  },

  {
    id: "warning-focus-ring",
    name: "Warning Focus Ring",
    category: "outline",
    description: "Amber focus ring for warning/caution states.",
    tags: ["focus", "warning", "outline", "accessibility", "ring"],
    previewType: "outline",
    targetState: "focused",
    recommendedFor: ["inputs", "selects", "warning forms"],
    payload: {
      outline: { outlineStyle: "solid", outlineWidth: 2, outlineOffset: 3, outlineColor: "rgba(245,158,11,0.95)" },
      // outline-color/width/offset are transitionable (KOA-17421); the ring
      // smooths in instead of snapping.
      transitions: [
        { property: "outline-color", duration: 140, timingFunction: "ease-out", delay: 0 },
        { property: "outline-width", duration: 140, timingFunction: "ease-out", delay: 0 },
        { property: "outline-offset", duration: 140, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },
  {
    id: "dotted-focus-ring",
    name: "Dotted Focus Ring",
    category: "outline",
    description: "Dotted keyboard-visible focus ring alternative.",
    tags: ["focus", "dotted", "outline", "keyboard", "accessibility"],
    previewType: "outline",
    targetState: "focused",
    recommendedFor: ["inputs", "buttons", "links"],
    payload: {
      outline: { outlineStyle: "dotted", outlineWidth: 2, outlineOffset: 4, outlineColor: "rgba(37,99,235,0.9)" },
      transitions: [
        { property: "outline-color", duration: 140, timingFunction: "ease-out", delay: 0 },
        { property: "outline-offset", duration: 140, timingFunction: "ease-out", delay: 0 }
      ]
    }
  },

  // 9. Utility ────────────────────────────────────────────────────────
  {
    id: "no-interaction",
    name: "No Interaction",
    category: "utility",
    description: "Disable pointer events; keep visuals.",
    tags: ["utility", "pointer-events", "interaction"],
    previewType: "utility",
    targetState: "base",
    payload: {
      basic: { pointerEvents: "none" }
    }
  },
  {
    id: "click-through",
    name: "Click Through",
    category: "utility",
    description: "Pass clicks through to children below.",
    tags: ["utility", "pointer-events", "overlay"],
    previewType: "utility",
    targetState: "base",
    recommendedFor: ["decorative overlays", "pseudo visual layers", "background effects"],
    payload: {
      basic: { pointerEvents: "none" }
    }
  },
  {
    id: "hidden-but-mounted",
    name: "Hidden But Mounted",
    category: "utility",
    description: "Invisible but stays in the DOM.",
    tags: ["utility", "hidden", "opacity"],
    previewType: "utility",
    targetState: "base",
    payload: {
      basic: { opacity: 0, pointerEvents: "none" },
      transitions: [{ property: "opacity", duration: 180, timingFunction: "ease-out", delay: 0 }]
    }
  },

  // 10. Glow (drop-shadow) ───────────────────────────────────────────────
  {
    id: "soft-glow",
    name: "Soft Glow",
    category: "glow",
    description: "Subtle ambient glow around the element.",
    tags: ["glow", "drop-shadow", "soft", "ambient"],
    previewType: "glow",
    targetState: "base",
    recommendedFor: ["cards", "buttons", "icons", "selected states"],
    payload: {
      filters: [{ type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 10, color: "rgba(36,109,255,0.6)" }],
      transitions: [{ property: "filter", duration: 220, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "neon-glow",
    name: "Neon Glow",
    category: "glow",
    description: "Vivid layered neon glow.",
    tags: ["glow", "neon", "drop-shadow", "vivid"],
    previewType: "glow",
    targetState: "base",
    recommendedFor: ["call-to-action buttons", "badges", "hero accents"],
    payload: {
      filters: [
        { type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 4, color: "#246dff" },
        { type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 12, color: "#246dff" }
      ],
      transitions: [{ property: "filter", duration: 240, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "focus-glow",
    name: "Focus Glow",
    category: "glow",
    description: "Soft glow ring on focus.",
    tags: ["glow", "focus", "drop-shadow", "a11y"],
    previewType: "glow",
    targetState: "focused",
    recommendedFor: ["inputs", "buttons", "links"],
    payload: {
      filters: [{ type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 8, color: "rgba(36,109,255,0.7)" }],
      transitions: [{ property: "filter", duration: 160, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "hover-glow",
    name: "Hover Glow",
    category: "glow",
    description: "Glow blooms on hover.",
    tags: ["glow", "hover", "drop-shadow"],
    previewType: "glow",
    targetState: "hover",
    recommendedFor: ["cards", "product tiles", "icon buttons"],
    payload: {
      filters: [{ type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 14, color: "rgba(36,109,255,0.6)" }],
      transitions: [{ property: "filter", duration: 200, timingFunction: "ease-out", delay: 0 }]
    }
  },

  // 11. Motion (looping) ──────────────────────────────────────────────────
  // Always-on looping animations. The payload carries the same keyframe-stop
  // timeline the Looping Animations panel control produces, so applying one and
  // then opening the panel shows the matching preset selected and fully editable.
  // Web-only — native generators never read the effects model.
  {
    id: "motion-float",
    name: "Floating Card",
    category: "motion",
    description: "Gentle up-and-down hover that never stops.",
    tags: ["loop", "float", "motion", "infinite"],
    previewType: "card",
    targetState: "base",
    recommendedFor: ["cards", "hero images", "badges", "icons"],
    payload: {
      animations: [defaultLoopingAnimationItem("float")]
    }
  },
  {
    id: "motion-spin",
    name: "Slow Spin",
    category: "motion",
    description: "Continuous 360° rotation at a calm pace.",
    tags: ["loop", "spin", "rotate", "motion", "infinite"],
    previewType: "utility",
    targetState: "base",
    recommendedFor: ["loaders", "icons", "decorative shapes"],
    payload: {
      animations: [defaultLoopingAnimationItem("spin")]
    }
  },
  {
    id: "motion-heartbeat",
    name: "Heartbeat",
    category: "motion",
    description: "Double-thump scale pulse, like a beating heart.",
    tags: ["loop", "pulse", "scale", "motion", "infinite"],
    previewType: "utility",
    targetState: "base",
    recommendedFor: ["like buttons", "notification dots", "CTAs"],
    payload: {
      animations: [defaultLoopingAnimationItem("heartbeat")]
    }
  },
  {
    id: "motion-glow-pulse",
    name: "Glow Pulse",
    category: "motion",
    description: "Breathing drop-shadow glow that swells and fades.",
    tags: ["loop", "glow", "drop-shadow", "motion", "infinite"],
    previewType: "glow",
    targetState: "base",
    recommendedFor: ["CTAs", "live indicators", "featured tiles"],
    payload: {
      animations: [defaultLoopingAnimationItem("glow")]
    }
  },
  {
    id: "motion-ping",
    name: "Radar Ping",
    category: "motion",
    description: "Expanding ring that scales out and fades — a radar pulse.",
    tags: ["loop", "ping", "radar", "motion", "infinite"],
    previewType: "utility",
    targetState: "base",
    recommendedFor: ["map markers", "status dots", "attention cues"],
    payload: {
      animations: [defaultLoopingAnimationItem("ping")]
    }
  },
  {
    id: "motion-shimmer",
    name: "Shimmer",
    category: "motion",
    description: "Brightness sweep that makes a surface shimmer.",
    tags: ["loop", "shimmer", "brightness", "motion", "infinite"],
    previewType: "image",
    targetState: "base",
    recommendedFor: ["skeletons", "highlights", "image overlays"],
    payload: {
      animations: [defaultLoopingAnimationItem("shimmer")]
    }
  },
  {
    id: "motion-pulse",
    name: "Pulse",
    category: "motion",
    description: "Soft scale pulse that never stops.",
    tags: ["loop", "pulse", "scale", "motion", "infinite"],
    previewType: "glow",
    targetState: "base",
    recommendedFor: ["badges", "notification dots", "CTAs"],
    payload: {
      animations: [defaultLoopingAnimationItem("pulse")]
    }
  },
  {
    id: "motion-bounce",
    name: "Bounce",
    category: "motion",
    description: "Playful double bounce loop.",
    tags: ["loop", "bounce", "motion", "infinite"],
    previewType: "utility",
    targetState: "base",
    recommendedFor: ["scroll hints", "arrows", "onboarding cues"],
    payload: {
      animations: [defaultLoopingAnimationItem("bounce")]
    }
  },
  {
    id: "motion-attention-shake",
    name: "Attention Shake",
    category: "motion",
    description: "Shakes three times, then stops — grabs the eye without nagging.",
    tags: ["shake", "attention", "motion", "finite", "alert"],
    previewType: "utility",
    targetState: "base",
    recommendedFor: ["validation errors", "alerts", "blocked actions"],
    payload: {
      // First finite-loop preset: runs 3× on mount, then rests (badge "3×").
      animations: [{ ...defaultLoopingAnimationItem("shake"), infinite: false, iterationCount: 3 }]
    }
  },
  {
    id: "motion-wiggle",
    name: "Subtle Wiggle",
    category: "motion",
    description: "Tiny repeating rotation for gentle attention.",
    tags: ["loop", "wiggle", "rotate", "motion", "attention"],
    previewType: "utility",
    targetState: "base",
    recommendedFor: ["notification icons", "badges", "small CTAs"],
    payload: {
      animations: [
        {
          id: "preset-wiggle",
          presetKey: "custom",
          durationMs: 1800,
          timingFunction: "ease-in-out",
          infinite: true,
          direction: "normal",
          keyframes: [
            { offset: 0, transforms: [{ id: "wg-0", type: "rotate", z: 0, unit: "deg" }] },
            { offset: 10, transforms: [{ id: "wg-1", type: "rotate", z: -3, unit: "deg" }] },
            { offset: 20, transforms: [{ id: "wg-2", type: "rotate", z: 3, unit: "deg" }] },
            { offset: 30, transforms: [{ id: "wg-3", type: "rotate", z: 0, unit: "deg" }] },
            { offset: 100, transforms: [{ id: "wg-4", type: "rotate", z: 0, unit: "deg" }] }
          ]
        }
      ]
    }
  },
  {
    id: "motion-breathe",
    name: "Breathing Opacity",
    category: "motion",
    description: "Calm breathing opacity loop for ambient status.",
    tags: ["loop", "breathing", "opacity", "motion", "calm"],
    previewType: "glow",
    targetState: "base",
    recommendedFor: ["status dots", "loading hints", "ambient indicators"],
    payload: {
      animations: [
        {
          id: "preset-breathe",
          presetKey: "custom",
          durationMs: 2200,
          timingFunction: "ease-in-out",
          infinite: true,
          direction: "alternate",
          keyframes: [
            { offset: 0, opacity: 0.55 },
            { offset: 100, opacity: 1 }
          ]
        }
      ]
    }
  },

  // 12. Status (feedback) ─────────────────────────────────────────────────
  // Semantic-colored drop-shadow glows for status/feedback UI. The dot sample
  // is neutral so the halo color carries the meaning.
  {
    id: "success-glow",
    name: "Success Glow",
    category: "status",
    description: "Soft green glow for positive status.",
    tags: ["glow", "success", "status", "green", "feedback"],
    previewType: "glow",
    targetState: "base",
    recommendedFor: ["success badges", "status icons", "confirmation UI"],
    payload: {
      filters: [{ type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 10, color: "rgba(34,197,94,0.6)" }],
      transitions: [{ property: "filter", duration: 220, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "danger-glow",
    name: "Danger Glow",
    category: "status",
    description: "Red glow for destructive or critical status.",
    tags: ["glow", "danger", "error", "status", "red"],
    previewType: "glow",
    targetState: "base",
    recommendedFor: ["error badges", "danger buttons", "critical alerts"],
    payload: {
      filters: [{ type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 10, color: "rgba(239,68,68,0.6)" }],
      transitions: [{ property: "filter", duration: 220, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "warning-glow",
    name: "Warning Glow",
    category: "status",
    description: "Amber glow for caution states.",
    tags: ["glow", "warning", "caution", "status", "amber"],
    previewType: "glow",
    targetState: "base",
    recommendedFor: ["warning badges", "pending states", "caution UI"],
    payload: {
      filters: [{ type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 10, color: "rgba(245,158,11,0.65)" }],
      transitions: [{ property: "filter", duration: 220, timingFunction: "ease-out", delay: 0 }]
    }
  },
  {
    id: "live-pulse",
    name: "Live Pulse",
    category: "status",
    description: 'Breathing green glow — "live" / online indicator.',
    tags: ["glow", "live", "online", "status", "loop", "infinite"],
    previewType: "glow",
    targetState: "base",
    recommendedFor: ["live indicators", "online dots", "recording badges"],
    payload: {
      animations: [
        {
          id: "preset-live-pulse",
          presetKey: "custom",
          durationMs: 1800,
          timingFunction: "ease-in-out",
          infinite: true,
          direction: "normal",
          keyframes: [
            {
              offset: 0,
              filters: [
                { id: "lp-0", type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 2, color: "rgba(34,197,94,0.5)" }
              ]
            },
            {
              offset: 50,
              filters: [
                { id: "lp-1", type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 14, color: "rgba(34,197,94,0.9)" }
              ]
            },
            {
              offset: 100,
              filters: [
                { id: "lp-2", type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 2, color: "rgba(34,197,94,0.5)" }
              ]
            }
          ]
        }
      ]
    }
  }
];

export const EFFECT_PRESET_CATEGORY_LABELS: EffectPresetCategoryLabel[] = [
  { value: "basic", label: "Basic" },
  { value: "hover", label: "Hover" },
  { value: "reveal", label: "Reveal" },
  { value: "cards", label: "Cards" },
  { value: "image", label: "Image" },
  { value: "text", label: "Text" },
  { value: "threeD", label: "3D" },
  { value: "outline", label: "Focus" },
  { value: "utility", label: "Utility" },
  { value: "motion", label: "Motion" },
  { value: "glow", label: "Glow" },
  { value: "status", label: "Status" }
];

/**
 * The presets that can actually do something on this target.
 *
 * <p>A preset is a shortcut into the same `IStylingEffects` fields the panel edits, applied to one
 * {@link EffectPreset.targetState}. So whether it renders is decided by that state, not by its category —
 * `pressed-depth` sits in the "hover" category but targets `pressed`, and filtering by category would both
 * hide it and keep the genuinely hover-targeted presets that live in other categories.</p>
 *
 * <p>`hoverApplies` is false on a mobile screen: neither React Native nor the native mobile generators have
 * a hover state, and a touch screen has no moment at which one would apply — the styling panel does not
 * project `hover` to those targets at all. Offering 16 cards that provably do nothing there is worse than
 * offering fewer, so they are dropped from the browser rather than left to disappoint on device.</p>
 */
export function selectAvailableEffectPresets(hoverApplies: boolean): EffectPreset[] {
  if (hoverApplies) {
    return EFFECTS_PRESETS;
  }
  return EFFECTS_PRESETS.filter((preset: EffectPreset): boolean => preset.targetState !== "hover");
}
